<!--
  A GButton that will trigger an async action and display a loading state until
  that action is complete.

  NOTE: The click listener function must return a Promise, which controls the
  loading state of the button.
-->
<script setup lang="ts">import { computed as _computed, ref as _ref } from 'vue';
const explicitIconSize = __MACROS_toRef(__props, "iconSize");
const explicitSize = __MACROS_toRef(__props, "size");

import { toRef as __MACROS_toRef } from "vue";
import type { IconDefinition } from '@fortawesome/fontawesome-common-types'
import { preserveWidthWhile } from '@shared/composables'
import { useListeners } from '@shared/vue'
import { isFunction } from '@shared/functions'
import { showError } from '@consumer/services/flash'
import { iconSizeForButton, loadingIconSizeForButton, type IconSize } from '@consumer/logic/icons'
import type { ButtonSize } from './GButton.vue'
import GButton from './GButton.vue'

withDefaults(defineProps<{
  // Text content to render inside the button, or false if it has no label.
  label?: string | false

  // The icon to render inside the button.
  icon?: IconDefinition

  // The icon to render inside the button, to the right.
  iconRight?: IconDefinition

  // The size of the icon, if different from the default for the button size.
  iconSize?: IconSize

  // Whether this button has no background.
  flat?: boolean

  // The size of the input.
  size?: ButtonSize

  // The button behaves as an icon button, with no label.
  iconButton?: boolean
}>(), {  })

defineEmits(['click'])

const size = _computed(() => __props.size ?? (__props.flat ? 'xsmall' : 'large'))
const iconSize = _computed(() => __props.iconSize || iconSizeForButton(size.value, __props.flat))

// Whether the action is in progress, in which case the button is disabled and
// displays a loading indicator.
let inProgress = _ref(false)

const instance = getCurrentInstance()

const onClick = useListeners(({ onClick }) => {
  if (isFunction(onClick))
    return onClick
  console.error('You must provide a @click listener in order to use PromiseButton', instance)
})

// Ignores duplicate clicks, and displays a loading indicator until complete.
function performAsyncAction (event: Event) {
  if (inProgress.value) return

  inProgress.value = true
  const promise = onClick?.(event)
  if (promise?.finally)
    promise.catch(showError).finally(() => { inProgress.value = false })
  else
    console.error('@click listeners in GPromiseButton must return a promise', { promise, instance })
}

const button = ref()
preserveWidthWhile(button, () => inProgress.value)

</script>

<template>
  <GButton
    ref="button"
    :name="label || undefined"
    :size="size"
    :flat="flat"
    :class="{ 'icon-button': iconButton, 'relative': flat && !icon && inProgress }"
    :color="iconButton ? 'transparent' : undefined"
    v-bind="$attrs"
    @click="performAsyncAction"
  >
    <LoadingIndicator
      v-if="inProgress"
      :class="{ 'absolute inset-0 mx-auto h-full': flat && !icon }"
      :size="loadingIconSizeForButton(size, flat)"
    />
    <GIcon v-else-if="icon" :icon="icon" :size="iconSize"/>
    <slot :inProgress="inProgress">
      <span
        v-if="!iconButton && label && (icon || !inProgress || flat)"
        :class="{'opacity-20': flat && !icon && inProgress }"
      >{{ label }}
      </span>
    </slot>
    <GIcon v-if="iconRight && !inProgress" :icon="iconRight" :size="iconSize"/>
  </GButton>
</template>
